import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth/contexts/AuthContext';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ClientMedical {
  id?: string;
  client_id: string;
  allergies: string[];
  conditions: string[];
  medications: string;
  is_pregnant: boolean;
  skin_reactions: string;
  pain_tolerance: number | null;
}

export interface ClientPhoto {
  id: string;
  client_id: string;
  session_id: string | null;
  storage_path: string;
  type: 'tattoo' | 'reference' | 'profile';
  caption: string;
  created_at: string;
  url?: string; // signed URL resolved at runtime
}

export interface ClientContract {
  id: string;
  template_name: string;
  created_at: string;
  pdf_url?: string | null;
  language: string;
}

export interface ClientSession {
  id: string;
  project_name: string;
  status: string;
  notes: string;
  started_at: string;
  ended_at: string | null;
  photos: string[];
}

// ─── Hook: medical data ───────────────────────────────────────────────────────

export function useClientMedical(clientId: string | undefined) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['client-medical', clientId],
    enabled: !!clientId && !!user,
    queryFn: async (): Promise<ClientMedical | null> => {
      const { data, error } = await supabase
        .from('client_medical')
        .select('*')
        .eq('client_id', clientId!)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return {
        id: data.id,
        client_id: data.client_id,
        allergies: (data.allergies as string[]) || [],
        conditions: (data.conditions as string[]) || [],
        medications: data.medications || '',
        is_pregnant: data.is_pregnant || false,
        skin_reactions: data.skin_reactions || '',
        pain_tolerance: data.pain_tolerance ?? null,
      };
    },
  });

  const upsertMedical = useMutation({
    mutationFn: async (data: Omit<ClientMedical, 'id'>) => {
      const { error } = await supabase
        .from('client_medical')
        .upsert(
          { ...data, owner_id: user!.id, updated_at: new Date().toISOString() },
          { onConflict: 'client_id' }
        );
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['client-medical', clientId] }),
  });

  return { ...query, medical: query.data, upsertMedical };
}

// ─── Hook: contracts ──────────────────────────────────────────────────────────

export function useClientContracts(clientId: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['client-contracts', clientId],
    enabled: !!clientId && !!user,
    queryFn: async (): Promise<ClientContract[]> => {
      const { data, error } = await supabase
        .from('signed_contracts')
        .select('id, template_name, signed_at, language')
        .eq('owner_id', user!.id)
        .eq('client_id', clientId!)
        .order('signed_at', { ascending: false });
      if (error) throw error;
      return (data || []).map((c: any) => ({
        id: c.id,
        template_name: c.template_name,
        created_at: c.signed_at,
        pdf_url: null,
        language: c.language,
      })) as ClientContract[];
    },
  });
}

// ─── Hook: sessions ───────────────────────────────────────────────────────────

export function useClientSessions(clientId: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['client-sessions', clientId],
    enabled: !!clientId && !!user,
    queryFn: async (): Promise<ClientSession[]> => {
      const { data, error } = await supabase
        .from('sessions')
        .select('id, project_name, status, notes, started_at, ended_at, photos')
        .eq('owner_id', user!.id)
        .eq('client_id', clientId!)
        .order('started_at', { ascending: false });
      if (error) throw error;
      return (data || []).map((s: any) => ({
        id: s.id,
        project_name: s.project_name || '',
        status: s.status,
        notes: s.notes || '',
        started_at: s.started_at,
        ended_at: s.ended_at,
        photos: s.photos || [],
      }));
    },
  });
}

// ─── Hook: photos ─────────────────────────────────────────────────────────────

export function useClientPhotos(clientId: string | undefined) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['client-photos', clientId],
    enabled: !!clientId && !!user,
    queryFn: async (): Promise<ClientPhoto[]> => {
      const { data, error } = await supabase
        .from('client_photos')
        .select('*')
        .eq('client_id', clientId!)
        .order('created_at', { ascending: false });
      if (error) throw error;

      // Resolve signed URLs (private bucket)
      const photosWithUrls = await Promise.all(
        (data || []).map(async (p: any) => {
          const { data: signedData } = await supabase.storage
            .from('session-photos')
            .createSignedUrl(p.storage_path, 3600); // 1 hour
          return {
            id: p.id,
            client_id: p.client_id,
            session_id: p.session_id,
            storage_path: p.storage_path,
            type: p.type,
            caption: p.caption || '',
            created_at: p.created_at,
            url: signedData?.signedUrl || '',
          };
        })
      );
      return photosWithUrls;
    },
  });

  const uploadPhoto = useMutation({
    mutationFn: async ({ file, type, caption }: { file: File; type: string; caption: string }) => {
      const ext = file.name.split('.').pop();
      const path = `${user!.id}/clients/${clientId}/${Date.now()}.${ext}`;
      const { error: uploadErr } = await supabase.storage.from('session-photos').upload(path, file);
      if (uploadErr) throw uploadErr;
      const { error: dbErr } = await supabase.from('client_photos').insert({
        client_id: clientId,
        owner_id: user!.id,
        storage_path: path,
        type,
        caption,
      });
      if (dbErr) throw dbErr;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['client-photos', clientId] }),
  });

  const deletePhoto = useMutation({
    mutationFn: async ({ id, storage_path }: { id: string; storage_path: string }) => {
      await supabase.storage.from('session-photos').remove([storage_path]);
      const { error } = await supabase.from('client_photos').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['client-photos', clientId] }),
  });

  return { ...query, photos: query.data || [], uploadPhoto, deletePhoto };
}
